import { $, el } from '../state/store.js';
import { on } from '../state/bus.js';
import { isConfigured } from '../services/supabase.js';
import { currentUser, isAnonymous, signOut } from '../services/auth.js';
import { isAdmin } from '../services/admin.js';
import { initAccount, showSignIn } from './account.js';
import { mountAdminPage } from './admin.js';
import { logoLockup } from './logo.js';
import { mountAdminApp } from './admin-mobile.js';

/* THE ADMIN PAGE — ?admin — on any device.
 *
 * On a wide window it is the desktop admin as a full page (ui/admin.js,
 * mountAdminPage); on a phone it is the purpose-built app (admin-mobile.js).
 * The rest of this comment is about the phone, which is why the path exists at
 * all: the wall it steps round, and what it keeps out.
 *
 * The app is desktop-only and says so at the door (ui/mobile-notice.js): the
 * two things it exists for — dragging four corners onto a photograph, orbiting
 * a scene while judging depth — cannot be done with a finger at all. None of
 * that applies to Admin. Reading a trial request, granting a licence and
 * answering a bug report are list-and-button work, and being unable to do them
 * away from a desk is a real cost with no matching reason.
 *
 * So this is a THIRD boot path, reached only by ?admin, and it is deliberately
 * not "the app, but allowed on mobile":
 *
 *   · no WebGL, no scene, no fonts, no timeline — none of it is imported on
 *     this path, so a phone loads a fraction of the bundle and never starts a
 *     renderer it has no use for;
 *   · the app shell stays hidden, so there is nothing to wander into;
 *   · the ONLY thing reachable is the Admin panel. This bypasses the desktop
 *     wall, and a bypass that also let somebody into the builder would be a
 *     hole in that wall rather than a door beside it.
 *
 * Access control is unchanged and is not this file's job: every query Admin
 * makes answers to is_admin() in the database (0007/0010). Someone opening
 * ?admin without an admin account gets an empty screen and a sentence, because
 * that is what the server will give them.
 */

/* The gate screens carry the same mark and wordmark the signed-in header does
   (admin-mobile.js's .mtop). Without it these were the one place in the whole
   path with no branding at all, so arriving here — a lapsed session on a
   phone, most often — read as a stray page rather than as Illusio3D asking
   who you are. The lockup replaces the bare <h2>, since repeating "Admin"
   under a wordmark that already says it is noise. */
function brand() {
  const bar = el('div', 'agate');
  const h = el('h2', 'mtop__h');
  h.appendChild(logoLockup());
  h.appendChild(el('span', 'mtop__sub', 'Admin'));
  bar.appendChild(h);
  return bar;
}

function card(...nodes) {
  const wrap = el('div', 'authwrap');
  wrap.id = 'adminonlywrap';
  const c = el('div', 'authcard');
  c.appendChild(brand());
  nodes.forEach(n => c.appendChild(n));
  wrap.appendChild(c);
  document.body.appendChild(wrap);
  return wrap;
}

/* Wide enough for the full page: the desktop panel's rows carry four or five
   actions side by side and want roughly this much room (admin.js). Below it the
   phone app's cards and bottom sheet are the right shape. */
const WIDE = window.matchMedia('(min-width: 900px)');

const clear = () => { $('#adminonlywrap')?.remove(); };

async function paint() {
  clear();

  if (!isConfigured) {
    card(el('p', 'authnote',
      'This build has no Supabase credentials, so there is nothing to administer.'));
    return;
  }

  const user = currentUser();
  if (!user || isAnonymous()) {
    const p = el('p', 'authnote',
      'Sign in with the account that administers this project.');
    const row = el('div', 'authrow');
    const go = el('button', 'btn', 'Sign in');
    go.onclick = () => showSignIn();
    row.appendChild(go);
    card(p, row);
    return;
  }

  /* Asked of the database rather than assumed from being signed in — the same
     predicate every query behind this screen answers to, so what is shown
     agrees with what will actually be permitted. */
  const ok = await isAdmin().catch(() => false);
  if (!ok) {
    card(el('p', 'authnote',
      'Signed in as ' + user.email + ', which does not administer this project.'),
      el('p', 'note', 'If that is the wrong account, sign out from a desktop and back in here.'));
    return;
  }

  const host = el('div');
  host.id = 'adminonlywrap';
  document.body.appendChild(host);
  mountAdminScreen(host, user.email);
}

/**
 * Fill `host` with the admin screen in the shape this window wants.
 *
 * WHICH SHAPE, decided by the window and not by the device: a full page for a
 * wide window, the purpose-built phone app for a narrow one. Same URL either
 * way — it is one page that fits itself, and resizing across the line
 * switches it live (see bootAdminOnly).
 *
 * The phone app is not the desktop panel restyled. See admin-mobile.js for why
 * that was the wrong answer: its person row carries five actions side by side
 * because it has 1040px to do it in, and at 390px that is a block of
 * near-identical targets with two destructive ones in it. Cards to scan, a
 * sheet to act, tabs where the thumb is.
 */
export function mountAdminScreen(host, email) {
  if (WIDE.matches) mountAdminPage(host, { email, onSignOut: () => { signOut(); } });
  else mountAdminApp(host);
}

export async function bootAdminOnly() {
  document.documentElement.classList.add('admin-only');
  /* Hidden rather than removed, for the reason mobile-notice.js gives: work
     already in flight paints into nodes it expects to still exist. */
  document.querySelector('.app')?.classList.add('hidden');

  /* Repaint whenever the session changes — signing in from the card above is
     the whole reason this screen has more than one state. */
  on('auth:changed', () => { paint(); });
  /* Crossing the line rebuilds the screen in the other shape — a window
     dragged narrower, a tablet turned on its side. */
  WIDE.addEventListener('change', () => { paint(); });

  await initAccount().catch(err => {
    console.error('[illusio] admin-only auth failed', err);
  });
  await paint();
}
